import type { CSSProperties } from 'react';
import { Button, Footnote, Group, Notice, Row, SectionLabel } from '@store/ui';
import { formatMoney, type MoneyJson, type ProductSummary } from '../../api';
import { ProductRow } from '../shared';
import type { VariantDraft } from './variants';
import { toMinorUnits } from './money';

/**
 * Предпросмотр — the product as the shop will show it.
 *
 * The catalogue row is the real thing: `ProductRow` is the component the
 * category screen renders, imported rather than imitated, so the sold-out pill,
 * the struck-through old price and the two-line clamp are whatever they are in
 * the shop today and cannot drift out of step with it.
 *
 * The page below it is *not* the real thing yet, and that is a known gap. The
 * customer's product page lives in `screens/product.tsx` as one component that
 * also fetches a quote, lists payment methods and raises an invoice — none of
 * which may happen from an editor, and none of which this agent may refactor:
 * that file belongs to someone else this round. The report asks for a
 * presentational `ProductView` to be lifted out of it so this block can be
 * deleted and replaced by a call. Until then everything here is a direct
 * transcription of that screen's markup — same classes, same order, same
 * groups — so the two can be diffed by eye.
 */

export interface PreviewInput {
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
  readonly activationInstructions: string;
  readonly price: string;
  readonly listPrice: string;
  readonly currency: string;
  readonly decimals: number;
  readonly imageUrl: string;
  readonly available: number | null;
  readonly variants: readonly VariantDraft[];
  readonly attributes: ReadonlyArray<{ readonly name: string; readonly value: string }>;
  readonly issues: readonly string[];
}

/* A pasted picture is whatever aspect the owner's host serves; `contain` keeps
   a tall one from swallowing the preview and a wide one from being cropped into
   a different picture. */
const PREVIEW_IMAGE: CSSProperties = {
  display: 'block',
  width: '100%',
  maxHeight: 220,
  objectFit: 'contain',
  borderRadius: 'var(--radius-xs)',
};

const money = (typed: string, currency: string, decimals: number): MoneyJson | null => {
  const amount = toMinorUnits(typed, decimals);
  return amount === null ? null : { amount, currency, decimals };
};

export function PreviewSection({ input }: { readonly input: PreviewInput }) {
  const price = money(input.price, input.currency, input.decimals);
  const listPrice = money(input.listPrice, input.currency, input.decimals);
  const title = input.title.trim() || 'Без названия';

  if (!price) {
    return (
      <Notice tone="warning">
        Пока не указана базовая цена, показать товар глазами покупателя нельзя — цена есть на каждом
        экране, где он появляется.
      </Notice>
    );
  }

  const summary: ProductSummary = {
    id: 'preview',
    shortId: 'preview',
    title,
    subtitle: input.subtitle.trim() || null,
    categoryId: null,
    price,
    listPrice,
    isFeatured: false,
    available: input.available,
  };

  const live = input.variants.filter((variant) => !variant.removed && variant.isPublished);

  return (
    <div className="stack">
      {input.issues.length > 0 ? (
        <Notice tone="warning">
          <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
            {input.issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <section>
        <SectionLabel>В списке товаров</SectionLabel>
        <Group>
          {/* The catalogue's own row, not a copy of it. */}
          <ProductRow product={summary} onOpen={() => {}} />
        </Group>
      </section>

      <section>
        <SectionLabel>На странице товара</SectionLabel>
        <Group padded>
          <div className="stack">
            {input.imageUrl.trim() ? (
              <img
                className="preview__image"
                style={PREVIEW_IMAGE}
                src={input.imageUrl.trim()}
                alt=""
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            ) : null}

            <div>
              <h2 className="title">{title}</h2>
              {summary.subtitle ? <p className="muted">{summary.subtitle}</p> : null}
            </div>

            {input.description.trim() ? <p className="text">{input.description}</p> : null}

            {live.length > 0 ? (
              <section>
                <SectionLabel>Вариант</SectionLabel>
                <Group>
                  {live.map((variant, index) => {
                    const variantPrice = money(variant.price, input.currency, input.decimals);
                    return (
                      <Row
                        key={variant.key}
                        title={variant.title.trim() || `Вариант ${index + 1}`}
                        {...(variant.durationDays.trim()
                          ? { subtitle: `${variant.durationDays.trim()} дней` }
                          : {})}
                        trailing={
                          <span className="price">
                            {variantPrice ? formatMoney(variantPrice) : '—'}
                          </span>
                        }
                        selected={index === 0}
                      />
                    );
                  })}
                </Group>
              </section>
            ) : null}

            <section>
              <SectionLabel>Итого</SectionLabel>
              <Group>
                <Row title="Цена" trailing={<span className="price">{formatMoney(price)}</span>} />
                <Row
                  title={<strong>К оплате</strong>}
                  trailing={<span className="price--lg">{formatMoney(price)}</span>}
                />
              </Group>
              <Footnote>
                Итоговую сумму считает сервер: промокод, бонусы и курс появятся у покупателя.
              </Footnote>
            </section>

            {input.attributes.length > 0 ? (
              <section>
                <SectionLabel>Характеристики</SectionLabel>
                <Group>
                  {input.attributes.map((attribute) => (
                    <Row
                      key={attribute.name}
                      title={attribute.name}
                      trailing={<span className="muted">{attribute.value}</span>}
                    />
                  ))}
                </Group>
              </section>
            ) : null}

            {input.activationInstructions.trim() ? (
              <section>
                <SectionLabel>Как активировать</SectionLabel>
                <Group padded>
                  <p className="text">{input.activationInstructions}</p>
                </Group>
              </section>
            ) : null}

            {/* Dead on purpose: this is a picture of the shop, and a live button
                here would raise a real invoice against the owner's own account. */}
            <Button block disabled>
              Оплатить {formatMoney(price)}
            </Button>
          </div>
        </Group>
      </section>
    </div>
  );
}
